import { useEffect, useRef } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { adminApi } from '@/api/endpoints'
import { BackIcon, CheckIcon, ExternalIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { PageSpinner } from '@/components/ui/Feedback'
import { StatusPill } from '@/components/ui/StatusPill'
import { TimeBar, TimeLeft } from '@/features/live/Countdown'
import { LivePanel } from '@/features/live/LivePanel'
import { LiveQueueList } from '@/features/live/LiveQueueList'
import { LIVE_KEY, hostOf, reasonOf, subjectOf, useLiveQueue, useNow } from '@/features/live/live'
import { toast } from '@/store/toast'
import type { LiveItem } from '@/types/api'

/** How a wait that is no longer there ended, read from the application's timeline. */
type Ending = 'solved' | 'missed' | 'gone'

/**
 * One user's parked page, opened to clear the check on it.
 *
 * The page stays useful after the check is gone: it says how it ended and
 * offers the next one in the queue, so several can be cleared in a row
 * without going back to the list.
 */
export function LiveSolvePage() {
  const { handoffId = '' } = useParams()
  // Keyed by the check: opening the next one starts from nothing.
  return <Solve key={handoffId} handoffId={handoffId} />
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function Solve({ handoffId }: { handoffId: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const queue = useLiveQueue()

  const status = useQuery({
    queryKey: ['live', handoffId, 'status'],
    queryFn: () => adminApi.liveStatus(handoffId),
    refetchInterval: 2000,
    staleTime: 0,
    retry: false,
  })
  const waiting = Boolean(status.data?.waiting)
  const now = useNow(waiting)
  const elapsed = Math.max(0, Math.floor((now - status.dataUpdatedAt) / 1000))
  const remaining = status.data?.remainingSeconds == null ? null : Math.max(0, status.data.remainingSeconds - elapsed)
  const waited = (status.data?.waitedSeconds ?? 0) + elapsed

  // What this page was about, kept after the queue forgets it.
  const listed = queue.items.find((item) => item.handoffId === handoffId)
  const subject = useRef<LiveItem | null>(null)
  if (listed) subject.current = listed
  const item = listed ?? subject.current

  // Once it is no longer waiting, the server still knows for a while how it ended.
  const seenWaiting = useRef(false)
  if (waiting) seenWaiting.current = true
  const ending: Ending | null = waiting || !status.isSuccess ? null : status.data?.ended === 'solved' ? 'solved' : status.data?.ended === 'unsolved' ? 'missed' : seenWaiting.current ? 'solved' : 'gone'
  useEffect(() => {
    if (ending) void queryClient.invalidateQueries({ queryKey: LIVE_KEY })
    if (ending && UUID.test(handoffId)) void queryClient.invalidateQueries({ queryKey: ['application-events', handoffId] })
    if (ending && item?.kind === 'practice') void queryClient.invalidateQueries({ queryKey: ['practice'] })
    // `item` is read, not watched: this runs once, when the wait ends.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ending, queryClient, handoffId])

  const done = useMutation({
    mutationFn: () => adminApi.liveDone(handoffId),
    onSuccess: () => toast.success('Marked as done', 'The run is carrying on from this page.'),
    onError: () => toast.error('Could not mark it done', 'The page may have already moved on.'),
  })

  const others = queue.items.filter((other) => other.handoffId !== handoffId)
  const next = others[0]
  const who = item?.user?.fullName || item?.user?.email
  const about = item ? subjectOf(item) : null
  const reason = reasonOf(status.data?.reason ?? item?.reason)
  const host = hostOf(status.data?.url ?? item?.url)
  const assistant = status.data?.assistant

  return (
    <div className="-mx-4 sm:mx-0">
      <div className="mb-4 flex items-center gap-2 px-4 sm:px-0">
        <Link to="/live" className="inline-flex h-9 items-center gap-1.5 rounded-field pl-1.5 pr-3 text-[13.5px] font-medium text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink">
          <BackIcon className="h-4 w-4" />
          Live checks
        </Link>
        {others.length > 0 && <span className="num text-[12.5px] text-ink-faint">{others.length} more waiting</span>}
      </div>

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="min-w-0">
          <header className="mb-3 px-4 sm:px-0">
            <div className="flex min-w-0 items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <h1 className="min-w-0 truncate text-[19px] font-semibold tracking-[-0.01em] text-ink sm:text-[22px]">{who ?? 'Live check'}</h1>
                  {waiting && (
                    <StatusPill tone="warning" live>
                      {reason.label}
                    </StatusPill>
                  )}
                </div>
                {about && (
                  <p className="mt-0.5 truncate text-[13.5px] text-ink-muted">
                    {about.title}
                    {item?.companyName && <span> · {item.companyName}</span>}
                  </p>
                )}
              </div>
              {waiting && (
                <div className="shrink-0 text-right">
                  <TimeLeft remaining={remaining} waited={waited} className="text-[22px]" />
                  <p className="text-[11.5px] text-ink-faint">{remaining === null ? 'waiting' : 'left'}</p>
                </div>
              )}
            </div>
            {waiting && <TimeBar remaining={remaining} timeout={status.data?.timeoutSeconds ?? null} className="mt-3" />}
            {waiting && (
              <p className="mt-2.5 text-[13px] text-ink-soft">
                {reason.ask}
                {assistant && !assistant.you && <span className="text-info"> {assistant.name} is on this one too.</span>}
              </p>
            )}
          </header>

          {status.isLoading ? (
            <PageSpinner label="Opening the live page" />
          ) : waiting && status.data ? (
            <LivePanel key={handoffId} handoffId={handoffId} status={status.data} />
          ) : (
            <Ended
              ending={ending ?? (status.isError ? 'gone' : null)}
              practice={item?.kind === 'practice' || handoffId.startsWith('practice_')}
              userId={item?.kind === 'practice' ? undefined : item?.user?.id}
              next={next}
              onNext={() => next && navigate(`/live/${encodeURIComponent(next.handoffId)}`)}
            />
          )}

          {waiting && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-4 sm:px-0">
              <p className="min-w-0 flex-1 text-[12.5px] text-ink-muted">The run carries on by itself as soon as the page is clear.</p>
              <Button variant="ghost" size="sm" loading={done.isPending} onClick={() => done.mutate()} title="Only when the page is clear but the run has not noticed">
                Page is clear, carry on
              </Button>
            </div>
          )}
        </section>

        <aside className="min-w-0 space-y-4 px-4 sm:px-0">
          <div className="panel divide-y divide-line">
            <Fact label={item?.kind === 'practice' ? 'Started by' : 'User'}>
              {item?.user && item.kind === 'practice' ? (
                <span className="block truncate font-medium text-ink">{item.user.fullName || item.user.email}</span>
              ) : item?.user ? (
                <Link to={`/users/${item.user.id}`} className="block truncate font-medium text-ink hover:underline">
                  {item.user.fullName || item.user.email}
                </Link>
              ) : (
                '–'
              )}
              {item?.user?.fullName && <span className="block truncate text-[12.5px] text-ink-muted">{item.user.email}</span>}
            </Fact>
            <Fact label={item?.kind === 'portal' ? 'Job portal' : item?.kind === 'practice' ? 'What' : 'Applying for'}>
              <span className="block truncate font-medium text-ink">{about?.title ?? '–'}</span>
              {item?.companyName && <span className="block truncate text-[12.5px] text-ink-muted">{item.companyName}</span>}
            </Fact>
            <Fact label="Site">
              {host ? (
                <span className="inline-flex min-w-0 items-center gap-1.5 text-ink">
                  <span className="num truncate text-[13px]">{host}</span>
                  <ExternalIcon className="h-3.5 w-3.5 shrink-0 text-ink-faint" />
                </span>
              ) : (
                '–'
              )}
            </Fact>
            <Fact label="On it">{assistant ? (assistant.you ? 'You' : assistant.name) : waiting ? 'You' : '–'}</Fact>
          </div>
          <p className="text-[12px] leading-relaxed text-ink-faint">
            {item?.kind === 'practice'
              ? 'A practice check on the CAPTCHA maker\'s demo page. Tick the box and finish any picture puzzle; do not press the page\'s own Submit. It ends by itself when the check is clear.'
              : 'You see this page as the user\'s own run sees it. What you do here is noted on the user\'s timeline. Do only what the check asks.'}
          </p>

          {others.length > 0 && (
            <div className="panel overflow-hidden">
              <p className="label-eyebrow border-b border-line px-4 py-3">Up next</p>
              <LiveQueueList items={others.slice(0, 5)} dense />
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <span className="shrink-0 pt-px text-[12.5px] text-ink-muted">{label}</span>
      <span className="min-w-0 text-right text-[13.5px] text-ink-soft">{children}</span>
    </div>
  )
}

function Ended({ ending, practice, userId, next, onNext }: { ending: Ending | null; practice?: boolean; userId?: string; next?: LiveItem; onNext: () => void }) {
  if (ending === null) return <PageSpinner label="Checking how it ended" />
  const copy = {
    solved: {
      title: 'Cleared',
      body: practice ? 'The run noticed by itself that the check was clear. This is exactly what happens on a user\'s application.' : 'The check is clear and the run is carrying on by itself.',
      tone: 'bg-success/10 text-success',
    },
    missed: {
      title: 'Time ran out',
      body: practice ? 'The check was not cleared in time. On an application the run would have stopped here, with nothing sent. Start another to try again.' : 'Nobody cleared it in time, so this run stopped. Nothing was sent. Run it again from the user\'s page.',
      tone: 'bg-danger/10 text-danger',
    },
    gone: { title: 'Nothing waiting here', body: 'This check is no longer on the queue. It was cleared, or its time ran out.', tone: 'bg-ink/[0.06] text-ink-muted' },
  }[ending]
  return (
    <div className="mx-4 flex flex-col items-center rounded-card border border-line bg-surface px-5 py-12 text-center sm:mx-0">
      <span className={`mb-3 grid h-11 w-11 place-items-center rounded-full ${copy.tone}`}>
        <CheckIcon className="h-5 w-5" />
      </span>
      <h2 className="text-[16px] font-semibold text-ink">{copy.title}</h2>
      <p className="mt-1 max-w-sm text-[13.5px] leading-relaxed text-ink-muted">{copy.body}</p>
      <div className="mt-5 flex w-full flex-col items-stretch gap-2 xs:w-auto xs:flex-row">
        {next ? <Button onClick={onNext}>Next check</Button> : <Link to="/live" className="inline-flex h-11 items-center justify-center rounded-field bg-ink px-5 text-[15px] font-medium text-oncolor hover:bg-ink/90">Back to the queue</Link>}
        {userId && (
          <Link to={`/users/${userId}`} className="inline-flex h-11 items-center justify-center rounded-field border border-line bg-surface px-5 text-[15px] font-medium text-ink hover:bg-surface-muted">
            Open the user
          </Link>
        )}
      </div>
    </div>
  )
}
